import React, { useState, DragEvent, useEffect, useRef, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import ImageMarker, { Marker } from 'react-image-marker';

import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-circle.svg';
import PumpIcon from '../../../../assets/icons/gas-icon.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/plus.svg';
import BackgroundImage from '../../../../assets/images/background.png';
import { MarkerWithDevice } from '../../../../interface/lane-types-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import PageTitle from '../../../../shared-components/page-title/PageTitle';
import SelectField from '../../../../shared-components/select-field/SelectField';
import { permissionCheck } from '../../../../shared-functions/JWTDocodeFunction';
import stationManagement from '../../../../utils/api/station-management';

interface EditLaneStationProperties {
  onClose: () => void;
  initialLaneId: string;
  initialLaneName: string;
  initialPreview: string; // 🆕 Map URL or preview from backend
  initialMarkers: MarkerWithDevice[]; // 🆕 Marker list from backend
  onSaveLane: (
    updatedLane: { id: string; name: string; siteMapUrl: string; markers: MarkerWithDevice[] },
    rawFile: File | null,
  ) => Promise<void>;
  // eslint-disable-next-line react/require-default-props
  assignedDevices?: string[];
}

interface LaneValues {
  selectedDevice: string;
  siteMapFile: File | null;
  siteMapPreview: string | null;
}

/**
 * Check the Image Url for valid
 * @param {string} url - The url string
 * @returns {boolean} Whether the url is valid
 */
const isValidUrl = (url?: string): boolean => {
  if (!url) return false;
  try {
    return Boolean(new URL(url));
  } catch {
    return false;
  }
};

/**
 * Converts a File object to a Base64-encoded data URL string.
 * @param {any} file - The file to convert
 * @returns {any} A promise that resolves with the Base64 data URL
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    /**
     * onerror
     */
    reader.onload = () => resolve(reader.result as string);
    /**
     * onerror
     */
    reader.onerror = (error) => reject(error);
  });
}

/**
 * Handle drag over
 * @param {any} event - The drag event
 */
const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
  event.preventDefault();
};

/**
 * Prevent image drag
 * @param {any} event - The drag event
 */
const handlePreventDrag = (event: DragEvent<HTMLImageElement>) => {
  event.preventDefault();
};

/**
 * Edit Lane Station
 * @param {any} properties - The properties for the component
 * @returns {any} The rendered component
 */
function EditLaneStation(properties: EditLaneStationProperties) {
  const {
    onClose,
    initialLaneId,
    initialLaneName,
    initialPreview,
    initialMarkers,
    onSaveLane,
    assignedDevices,
  } = properties;
  const [laneName, setLaneName] = useState(initialLaneName || '');
  const [nameError, setNameError] = useState(false);
  const [laneValues, setLaneValues] = useState<LaneValues>({
    selectedDevice: '',
    siteMapFile: null,
    siteMapPreview: initialPreview || null,
  });
  const fileInputReference = useRef<HTMLInputElement | null>(null);
  const dispatch = useAppDispatch();
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const [selectDevices, setSelectDevices] = useState(false);
  const [markers, setMarkers] = useState<MarkerWithDevice[]>(initialMarkers || []); // Add this state if missing
  const [loading, setLoading] = useState(false);
  const [deviceOptions, setDeviceOptions] = useState<{ label: string; value: string }[]>([]);
  const [loadedMaps, setLoadedMaps] = useState<Record<string, boolean>>({});
  const { t, i18n } = useTranslation('sidePanel');

  /**
   * devices list take Api
   */
  const handleDevicesList = async () => {
    try {
      const response = await stationManagement.fetchDeviceList({
        chargingStation: selectedChargingPoint?.id,
      });
      const formattedDevices = response.data.map((device: any) => ({
        label: `${device.chargingPointName || ''}(${device.chargingPointSn || ''})`,
        value: device.chargingPointSn || '',
      }));

      setDeviceOptions(formattedDevices);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.status === 413
              ? 'Image size is too large!'
              : error?.response?.data?.message || 'Something went wrong!',
          type: 'error',
          code: error?.response?.status || 0,
        }),
      );
    }
  };

  /** Available devices (exclude already assigned ones) */
  const availableDeviceOptions = deviceOptions.filter(
    (device) =>
      !markers.some((marker) => marker.chargingPointSn === device.value) &&
      !(assignedDevices || []).includes(device.value),
  );

  /**
   * add lane
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleChangeFunction = (name: string, value: string) => {
    setLaneValues((previous) => ({ ...previous, [name]: value }));
    if (value) {
      setSelectDevices(false);
    }
  };

  /**
   * Drag & Drop for new image
   * @param {any} event - The event
   */
  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!event.dataTransfer.types.includes('Files')) {
      return;
    }
    const file = event.dataTransfer.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: t('invalidFileType', 'Only JPG and PNG files are allowed.'),
            type: 'error',
          }),
        );
        return;
      }
      if (file.size > 1 * 1024 * 1024) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: t('invalidFileSize', 'Files must be smaller than 1MB.'),
            type: 'error',
          }),
        );
        return;
      }
      const previewUrl = URL.createObjectURL(file);
      setLaneValues({
        selectedDevice: laneValues.selectedDevice,
        siteMapFile: file,
        siteMapPreview: previewUrl,
      });
      setMarkers([]);
    }
  };
  /**
   * Trigger hidden file input
   */
  const handleUploadClick = () => {
    fileInputReference.current?.click();
  };
  /**
   * Handle file upload (input)
   * @param {any} event - The event
   */
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: t('invalidFileType', 'Only JPG and PNG files are allowed.'),
            type: 'error',
          }),
        );
        return;
      }
      if (file.size > 1 * 1024 * 1024) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: t('invalidFileSize', 'Files must be smaller than 1MB.'),
            type: 'error',
          }),
        );
        return;
      }
      const previewUrl = URL.createObjectURL(file);
      setLaneValues({
        selectedDevice: laneValues.selectedDevice,
        siteMapFile: file,
        siteMapPreview: previewUrl,
      });
      setMarkers([]);
    }
  };

  /**
   * remove for the images and devices
   */
  const handleRemoveImage = () => {
    setLaneValues({
      selectedDevice: '',
      siteMapFile: null,
      siteMapPreview: '',
    });
    if (fileInputReference.current) {
      fileInputReference.current.value = '';
    }
  };

  /**
   * Add marker on image
   * @param {any} marker - The marker
   */
  const handleAddMarker = (marker: Marker) => {
    if (!laneValues.selectedDevice) {
      setSelectDevices(true);
      return;
    }

    const selectedDevice = deviceOptions.find(
      (device) => device.value === laneValues.selectedDevice,
    );

    const top = Number(marker.top);
    const left = Number(marker.left);

    const markerWithDevice: MarkerWithDevice = {
      top,
      left,
      device: selectedDevice?.label || laneValues.selectedDevice,
      chargingPointSn: selectedDevice?.value || '',
      position: {
        top,
        left,
      },
    };

    setMarkers((previous) => [...previous, markerWithDevice]);
    setLaneValues((previous) => ({ ...previous, selectedDevice: '' }));
  };

  /**
   * Remove marker
   * @param {any} markerToRemove - The markerToRemove
   */
  const handleRemoveMarker = (markerToRemove: MarkerWithDevice) => {
    setMarkers((previous) =>
      previous.filter(
        (m) =>
          m.top !== markerToRemove.top ||
          m.left !== markerToRemove.left ||
          m.device !== markerToRemove.device,
      ),
    );
  };

  /**
   * Save updated data
   */
  const handleSave = async () => {
    if (!laneName.trim()) {
      setNameError(true);
      return;
    }

    if (laneValues.siteMapFile && laneValues.siteMapFile.size > 900 * 1024) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('invalidFileSize', 'Files must be smaller than 900KB.'),
          type: 'error',
        }),
      );
      return;
    }
    setLoading(true);
    try {
      let siteMapUrl = laneValues.siteMapPreview || '';
      if (laneValues.siteMapFile) {
        siteMapUrl = await fileToBase64(laneValues.siteMapFile);
      }

      const updatedLane = {
        id: initialLaneId,
        name: laneName.trim(),
        siteMapUrl,
        markers,
      };

      await onSaveLane(updatedLane, laneValues.siteMapFile);
      onClose();
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.status === 413
              ? 'Image size is too large!'
              : error?.response?.data?.message || 'Something went wrong!',
          type: 'error',
          code: error?.response?.status || 0,
        }),
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    handleDevicesList();
  }, [selectedChargingPoint]);
  useEffect(() => {
    setTimeout(() => {
      setSelectDevices(false);
    }, 3000);
  }, [selectDevices]);
  return (
    <div className="add-lane-content" dir={i18n.dir()}>
      <div className="heading">
        <PageTitle
          title={t('editLane')}
          buttonSvgIcon={<CloseIcon />}
          buttonType="none"
          onClickButton={onClose}
          buttonLabel=" "
        />
      </div>

      <div className="lane-name-input-wrapper">
        <CustomInput
          inputLabel={t('laneNameLabel') || 'Lane Name'}
          type="text"
          placeholder={t('laneNamePlaceholder') || 'Enter Lane Name'}
          value={laneName}
          name="laneName"
          onChangeValue={(name, value) => {
            setLaneName(String(value));
            setNameError(false);
          }}
          valueRequired
          error={nameError}
          errorMessage={t('laneNameRequired') || 'Lane name is required'}
        />

        <SelectField
          options={availableDeviceOptions}
          placeholder={t('selectDevice')}
          selectedValue={laneValues.selectedDevice}
          name="selectedDevice"
          onChangeValue={handleChangeFunction}
          error={selectDevices}
          errorMessage={t('selectDeviceError')}
        />
        <div className="add-device">
          <MyButton
            label={t('save')}
            buttonType="submit"
            onClickFunc={handleSave}
            disableButton={
              !laneName.trim() ||
              !laneValues.siteMapPreview ||
              (!permissionCheck('partnerChargingStationUpdate') &&
                !permissionCheck('cpoChargingStationUpdate'))
            }
            loading={loading}
          />
        </div>
      </div>

      <div
        className="main-container"
        onDrop={laneValues.siteMapPreview ? undefined : handleDrop}
        onDragOver={laneValues.siteMapPreview ? undefined : handleDragOver}>
        {laneValues.siteMapPreview ? (
          <div className="images-container">
            <div
              role="button"
              className="remove-image-button"
              onClick={handleRemoveImage}
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  handleRemoveImage();
                }
              }}
              aria-label="Remove site map image">
              <CloseIcon />
            </div>
            <img
              src={laneValues.siteMapPreview}
              alt="preload"
              className="preload-image"
              onLoad={() => {
                const url = laneValues.siteMapPreview;
                if (url) {
                  setLoadedMaps((previous) => ({
                    ...previous,
                    [url]: true,
                  }));
                }
              }}
            />
            <ImageMarker
              src={
                isValidUrl(laneValues.siteMapPreview) && loadedMaps[laneValues.siteMapPreview]
                  ? laneValues.siteMapPreview
                  : BackgroundImage
              }
              markers={markers}
              onAddMarker={handleAddMarker}
              markerComponent={(proper: Marker) => {
                const m = proper as MarkerWithDevice;
                return (
                  <div className="marker-wrapper" draggable={false}>
                    <div className="device-marker" draggable={false}>
                      <img
                        src={PumpIcon}
                        alt=""
                        className="device-details-image"
                        draggable={false}
                        onDragStart={handlePreventDrag}
                      />
                      <p className="marker-device-label">
                        {deviceOptions.find((d) => d.value === m.chargingPointSn)?.label ||
                          m.device}
                      </p>
                      <div
                        role="button"
                        tabIndex={0}
                        className="remove-marker-button"
                        aria-label="remove-marker-button"
                        onClick={() => handleRemoveMarker(m)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') handleRemoveMarker(m);
                        }}>
                        <CloseIcon />
                      </div>
                    </div>
                    <div className="device-status-container online">
                      <div className="static-container" />
                      <div className="animation-container" />
                    </div>
                  </div>
                );
              }}
            />
          </div>
        ) : (
          <>
            <div
              className="upload-container"
              role="button"
              tabIndex={0}
              onClick={handleUploadClick}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') handleUploadClick();
              }}>
              <p className="upload">{t('dragDrop')}</p>
            </div>

            <div className="upload-footer">
              <p>{t('uploadSiteMapHint')}</p>
              <span
                className="upload-label"
                role="button"
                tabIndex={0}
                onClick={handleUploadClick}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') handleUploadClick();
                }}>
                <PlusIcon /> {t('uploadSiteMap')}
              </span>

              <input
                type="file"
                accept=".jpg, .jpeg, .png, image/jpeg, image/png, image/jpg"
                ref={fileInputReference}
                onChange={handleFileChange}
                className="file-input"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

EditLaneStation.defaultProps = {
  assignedDevices: [],
};

export default EditLaneStation;
